import React, { useState, useEffect } from 'react';
import { getPermissionSchema, getRoles, createRole, updateRole, deleteRole } from '../../lib/treeApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Plus, Trash2, Pencil, ShieldCheck, Lock } from 'lucide-react';
import { toast } from 'sonner';

const emptyPerms = (schema) => Object.fromEntries(
  Object.entries(schema).map(([page, meta]) => [page, { access: false, actions: Object.fromEntries(Object.keys(meta.actions).map(a => [a, false])) }])
);

function PermissionGrid({ schema, permissions, onChange, disabled }) {
  const setAccess = (page, access) => onChange({ ...permissions, [page]: { ...permissions[page], access, actions: access ? permissions[page].actions : Object.fromEntries(Object.keys(permissions[page].actions).map(a => [a, false])) } });
  const setAction = (page, action, val) => onChange({ ...permissions, [page]: { ...permissions[page], actions: { ...permissions[page].actions, [action]: val } } });

  return (
    <div className="space-y-3" data-testid="tree-permission-grid">
      {Object.entries(schema).map(([page, meta]) => (
        <div key={page} className="bg-[#F8F6F3] rounded-xl p-3.5">
          <label className="flex items-center gap-2.5 cursor-pointer">
            <Checkbox checked={permissions[page]?.access || false} disabled={disabled}
                      onCheckedChange={(v) => setAccess(page, !!v)} data-testid={`perm-${page}-access`} />
            <span className="font-semibold text-[#0A1711] font-['Outfit']">{meta.label}</span>
          </label>
          {Object.keys(meta.actions).length > 0 && (
            <div className="mt-2 ml-7 space-y-1.5">
              {Object.entries(meta.actions).map(([action, label]) => (
                <label key={action} className={`flex items-center gap-2.5 text-sm ${permissions[page]?.access ? 'text-[#4A5D53] cursor-pointer' : 'text-[#4A5D53]/40'}`}>
                  <Checkbox checked={permissions[page]?.actions?.[action] || false}
                            disabled={disabled || !permissions[page]?.access}
                            onCheckedChange={(v) => setAction(page, action, !!v)}
                            data-testid={`perm-${page}-${action}`} />
                  {label}
                </label>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function TreeRolesPage() {
  const [schema, setSchema] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [name, setName] = useState('');
  const [permissions, setPermissions] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([getPermissionSchema(), getRoles()]);
      setSchema(s.data);
      setRoles(r.data);
    } catch { toast.error('Failed to load roles'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setName(''); setPermissions(emptyPerms(schema)); setShowCreate(true); };
  const openEdit = (role) => { setEditingRole(role); setName(role.name); setPermissions(role.permissions); };

  const handleCreate = async () => {
    if (!name.trim()) return toast.error('Role name is required');
    try {
      await createRole({ name, permissions });
      toast.success('Role created');
      setShowCreate(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create role'); }
  };

  const handleSaveEdit = async () => {
    try {
      await updateRole(editingRole.key, { name, permissions });
      toast.success('Role updated');
      setEditingRole(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update role'); }
  };

  const handleDelete = async (role) => {
    try { await deleteRole(role.key); toast.success('Role deleted'); load(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete role'); }
  };

  if (loading || !schema) return <div className="text-[#4A5D53] text-sm">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="tree-roles-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#0A1711] font-['Outfit'] tracking-tight">Roles</h1>
          <p className="text-sm text-[#4A5D53] mt-1">Tick exactly what each role can see and do</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="bg-[#1A4331] text-white hover:bg-[#1A4331]/90 rounded-lg" data-testid="tree-create-role-button">
              <Plus className="w-4 h-4 mr-2" /> New Role
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="tree-create-role-dialog">
            <DialogHeader><DialogTitle className="font-['Outfit']">New Role</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Role Name</Label>
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Yard Supervisor" data-testid="tree-role-name-input" />
              </div>
              {permissions && <PermissionGrid schema={schema} permissions={permissions} onChange={setPermissions} />}
              <Button onClick={handleCreate} className="w-full bg-[#1A4331] text-white" data-testid="tree-submit-role-button">Create Role</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-3">
        {/* Admin — hardcoded, not editable */}
        <div className="bg-white border border-[#1A4331]/15 rounded-xl p-4 flex items-center justify-between" data-testid="tree-role-row-admin">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#1A4331]/10 flex items-center justify-center"><Lock className="w-4 h-4 text-[#1A4331]" /></div>
            <div>
              <p className="font-semibold text-[#0A1711] font-['Outfit']">Admin</p>
              <p className="text-xs text-[#4A5D53]">Full access to everything, including Users &amp; Settings — can't be changed</p>
            </div>
          </div>
        </div>

        {roles.map((role) => (
          <div key={role.key} className="bg-white border border-[#1A4331]/15 rounded-xl p-4 flex items-center justify-between" data-testid={`tree-role-row-${role.key}`}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#1A4331]/10 flex items-center justify-center"><ShieldCheck className="w-4 h-4 text-[#1A4331]" /></div>
              <div>
                <p className="font-semibold text-[#0A1711] font-['Outfit']">{role.name}</p>
                <p className="text-xs text-[#4A5D53]">
                  {Object.entries(role.permissions).filter(([, p]) => p.access).map(([page]) => schema[page]?.label).join(', ') || 'No access ticked yet'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => openEdit(role)} className="text-[#4A5D53] hover:text-[#1A4331]" data-testid={`tree-edit-role-${role.key}`}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              {!role.is_builtin && (
                <Button variant="ghost" size="sm" onClick={() => handleDelete(role)} className="text-red-400 hover:text-red-600" data-testid={`tree-delete-role-${role.key}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editingRole} onOpenChange={(open) => { if (!open) setEditingRole(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="tree-edit-role-dialog">
          <DialogHeader><DialogTitle className="font-['Outfit']">Edit Role</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Role Name</Label>
              <Input value={name} onChange={e => setName(e.target.value)} data-testid="tree-edit-role-name-input" />
            </div>
            {permissions && <PermissionGrid schema={schema} permissions={permissions} onChange={setPermissions} />}
            <DialogFooter>
              <Button onClick={handleSaveEdit} className="w-full bg-[#1A4331] text-white" data-testid="tree-save-role-button">Save Changes</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
